/**
 * Runs: the case's Auto-Investigate runs (GET /cases/{id}/runs), newest first.
 *
 * Presentational: the case page owns loading and refresh. A run that is still
 * going shows a running badge and its latest step; when the page is watching
 * that job, the live progress (`liveJob`) replaces the progress the list
 * carried. "Open report" opens the printable case report for that run in a new
 * tab; a run without a recorded event (queued, running, failed) has no report.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { History, Loader2, RefreshCw, FileText, Layers, AlertTriangle } from 'lucide-react';
import type { CanvasJobView, CaseRun } from '../../services/api';
import { getUiLocation } from '../tour/uiLocations';
import {
    describeJobProgress, formatDurationSeconds, formatReadiness, formatRunTimestamp, isRunActive,
    runCanvasUrl, runDisplayStatus, runReportUrl, runStatusMeta, secondsSince, shortId,
} from './caseRuns';

export interface CaseRunsSectionProps {
    caseId: string;
    runs: CaseRun[];
    loading: boolean;
    error: string | null;
    onRefresh: () => void;
    /** The job the page is watching; its progress overrides the listed one. */
    liveJob?: CanvasJobView | null;
    /** Clock for the elapsed time of running rows (the page ticks it). */
    now?: number;
    /** Stop a queued or running job. Shown only on an active row that has a job id. */
    onCancel?: (run: CaseRun) => void;
}

const num = (v: number | null | undefined) =>
    (typeof v === 'number' && Number.isFinite(v) ? v.toLocaleString('en-IN') : '—');

interface RunRowProps {
    caseId: string;
    run: CaseRun;
    liveJob?: CanvasJobView | null;
    now: number;
    onCancel?: (run: CaseRun) => void;
}

function RunRow({ caseId, run, liveJob, now, onCancel }: RunRowProps) {
    const live = liveJob && run.job_id && liveJob.job_id === run.job_id ? liveJob : null;
    const status = live && !run.event_id ? live.status : runDisplayStatus(run);
    const meta = runStatusMeta(status);
    const active = live ? meta.running : isRunActive(run);
    const progress = describeJobProgress(live?.progress ?? run.progress);
    const reportUrl = runReportUrl(caseId, run);
    const duration = active
        ? formatDurationSeconds(secondsSince(run.started_at, now))
        : formatDurationSeconds(run.duration_s);
    const readiness = run.readiness !== undefined && run.readiness !== null ? formatReadiness(run.readiness) : null;
    const error = live?.error || run.error;

    return (
        <li className="px-4 py-3 hover:bg-secondary/20" data-testid="case-run-row" data-run-status={status || ''}>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <span
                    data-testid="case-run-status"
                    className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-full border ${meta.tone}`}
                >
                    {meta.running && <Loader2 className="w-3 h-3 animate-spin" aria-hidden />}
                    {meta.label}
                </span>
                <span className="text-sm" title={run.started_at || undefined}>
                    {formatRunTimestamp(run.started_at || run.recorded_at)}
                </span>
                <span className="text-xs text-muted-foreground" data-testid="case-run-duration">
                    {active ? `${duration} so far` : duration}
                </span>
                {run.budget_capped && (
                    <span
                        className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-700 dark:text-amber-300 border border-amber-500/30"
                        title="The run stopped at its time or seed budget; results are partial"
                    >
                        budget capped
                    </span>
                )}
                <div className="ml-auto flex items-center gap-1.5">
                    {active && run.job_id && onCancel && (
                        <button
                            type="button"
                            data-testid="case-run-cancel"
                            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-red-500/40 text-red-600 dark:text-red-300 hover:bg-red-500/10"
                            onClick={() => onCancel(run)}
                        >
                            Cancel
                        </button>
                    )}
                    {reportUrl ? (
                        <a
                            href={reportUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="case-run-open-report"
                            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-primary/10 text-primary hover:bg-primary/20"
                        >
                            <FileText className="w-3 h-3" aria-hidden /> Open report
                        </a>
                    ) : (
                        <span
                            data-testid="case-run-open-report"
                            aria-disabled="true"
                            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-secondary text-muted-foreground opacity-60 cursor-not-allowed"
                            title="The report is available once the run has been recorded"
                        >
                            <FileText className="w-3 h-3" aria-hidden /> Open report
                        </span>
                    )}
                    <Link
                        to={runCanvasUrl(caseId, run)}
                        data-testid="case-run-open-canvas"
                        className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-secondary hover:bg-secondary/80"
                    >
                        <Layers className="w-3 h-3" aria-hidden /> Open canvas
                    </Link>
                </div>
            </div>
            <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-[11px] text-muted-foreground">
                <span>Seeds <span className="text-foreground/80">{num(run.seed_count)}</span></span>
                <span>Nodes <span className="text-foreground/80">{num(run.node_count)}</span></span>
                <span>Edges <span className="text-foreground/80">{num(run.edge_count)}</span></span>
                {readiness && (
                    <span data-testid="case-run-readiness">Readiness <span className="text-foreground/80">{readiness}</span></span>
                )}
                <span className="font-mono" title={run.job_id ? `Job ${run.job_id}` : 'Not run as a job'}>
                    Job {shortId(run.job_id)}
                </span>
                {run.event_id && (
                    <span className="font-mono" title={`Run record ${run.event_id}`}>Record {shortId(run.event_id)}</span>
                )}
            </div>
            {active && progress && (
                <p className="mt-1 text-[11px] text-primary truncate" data-testid="case-run-progress" aria-live="polite">
                    {progress}
                </p>
            )}
            {error && !active && (
                <p className="mt-1 text-[11px] text-red-500 flex items-start gap-1" data-testid="case-run-error">
                    <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" aria-hidden /> {error}
                </p>
            )}
        </li>
    );
}

export const CaseRunsSection: React.FC<CaseRunsSectionProps> = ({ caseId, runs, loading, error, onRefresh, liveJob, now, onCancel }) => {
    const [mountedAt] = useState(() => Date.now());
    const clock = now ?? mountedAt;
    return (
        <div className="bg-card border border-border rounded-lg overflow-hidden" data-tour="case-runs" data-testid="case-runs">
            <div className="flex items-center justify-between p-4 border-b border-border">
                <div className="flex items-center gap-2">
                    <History className="w-5 h-5 text-primary" aria-hidden />
                    <h3 className="font-medium">Runs</h3>
                    <span className="px-2 py-0.5 text-xs bg-secondary rounded-full">{runs.length}</span>
                </div>
                <button
                    type="button"
                    onClick={onRefresh}
                    disabled={loading}
                    className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-secondary hover:bg-secondary/80 disabled:opacity-60"
                    aria-label="Refresh runs"
                >
                    <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} aria-hidden /> Refresh
                </button>
            </div>
            {error ? (
                <div className="p-4 text-sm text-red-500 flex items-center gap-2" data-testid="case-runs-error">
                    <AlertTriangle className="w-4 h-4" aria-hidden /> {error}
                </div>
            ) : loading && runs.length === 0 ? (
                <div className="p-4 text-sm text-muted-foreground flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" aria-hidden /> Loading runs…
                </div>
            ) : runs.length === 0 ? (
                <div className="p-6 text-center text-sm text-muted-foreground" data-testid="case-runs-empty">
                    <p>No Auto-Investigate runs recorded for this case yet.</p>
                    <p className="text-xs mt-1">{getUiLocation('case-auto-investigate').where}</p>
                </div>
            ) : (
                <ul className="divide-y divide-border max-h-96 overflow-y-auto">
                    {runs.map((run, i) => (
                        <RunRow
                            key={run.event_id || run.job_id || run.run_id || `run-${i}`}
                            caseId={caseId}
                            run={run}
                            liveJob={liveJob}
                            now={clock}
                            onCancel={onCancel}
                        />
                    ))}
                </ul>
            )}
        </div>
    );
};

export default CaseRunsSection;
